import { Component, OnInit, Input, Output, ViewChild, OnChanges, SimpleChanges, forwardRef, Provider, EventEmitter } from '@angular/core';
import { UtilService } from '../../shared/util/util.service';
import { NameValue } from '../../shared/model/name-value.model';
import { AffCodeService } from '../shared/affcode.service';
import { AffCode } from '../shared/affcode.model';
import { AffCodeType } from '../shared/affcode-type.model';
import {
  ArrayStringValueAccessor,
  CreateArrayStringValueAccessorProvider,
  CreateArrayStringValueValidationProvider,
} from '../../shared/widgets/value-accessors/array-string-value-accessor';

@Component({
  selector: 'wl-affcode-selector',
  templateUrl: 'affcode-selector.component.html',
  styleUrls: ['affcode-selector.component.css'],
  providers: [
    CreateArrayStringValueAccessorProvider(AffCodeSelectorComponent),
    CreateArrayStringValueValidationProvider(AffCodeSelectorComponent),
  ],
  standalone: false
})
export class AffCodeSelectorComponent extends ArrayStringValueAccessor implements OnInit {
  @Input() public userDefinedAffCodesOnly: boolean = false;
  @Input() public showAllOfOneOfOptions: boolean = false;
  @Input() public allOfOneOfPromptLabel: string = '';
  @Input() public allOfSelected: boolean = false;
  @Output() public allOfSelectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public static UniqueId: number = 0;
  public UniqueId: number = 0;
  public _affCodeService: AffCodeService;
  public _utilService: UtilService;

  public selectedAffCodes: Array<AffCode>;
  public affCodeTypes: Array<AffCodeType>;
  public _allOfSelected: string = '1';

  public constructor(affCodeService: AffCodeService, utilService: UtilService) {
    super();

    this._affCodeService = affCodeService;
    this._utilService = utilService;

    this.selectedAffCodes = new Array<AffCode>();
    this.affCodeTypes = new Array<AffCodeType>();
    this.UniqueId = AffCodeSelectorComponent.UniqueId++;
  }

  ngOnInit() {
    var promise: Promise<Array<AffCodeType>> = null;

    if (this.userDefinedAffCodesOnly) promise = this._affCodeService.getUserDefinedAffCodeTypesAndAffCodes();
    else promise = this._affCodeService.getAllAffCodeTypesAndAffCodes(true);

    promise.then((data: Array<AffCodeType>) => {
      //add a selected to both aff code and affcodetype
      for (let act of data) {
        act.Selected = false;

        for (let ac of act.AffCodes) {
          ac.Selected = false;

          for (let sv of this.value) {
            if (this._utilService.toLowerTrim(sv) == this._utilService.toLowerTrim(ac.AffCode)) {
              let existingAc = new AffCode();
              this._utilService.mapSafe(ac, existingAc);

              //this.selectedAffCodes.push(existingAc);
            }
          }
        }
      }
      this.affCodeTypes = data.sort((a, b) => {
        const typeA = a.AffCodeType;
        const typeB = b.AffCodeType;

        if (typeA < typeB) {
          return -1;
        } else if (typeA > typeB) {
          return 1;
        } else {
          return 0;
        }
      });

      this.updateSelectedAffCodesFromValue();
    });
  }

  public addAffCodes = (): void => {
    for (let act of this.affCodeTypes) {
      for (let ac of act.AffCodes) {
        if (ac.Selected == true) {
          ac.Selected = false;
          this.addAffCode(ac);
          if (ac.ChildAffIds && ac.ChildAffIds.length) {
            let childAffs = this.affCodeTypes
              .map((x) => x.AffCodes.filter((y) => ac.ChildAffIds.some((id) => id === y.AffId)))
              .reduce((a, b) => a.concat(b));
            for (let childAff of childAffs) {
              this.addAffCode(childAff);
            }
          }
        }
      }
    }

    this.updateValueFromSelectedAffCodes();
  };

  private addAffCode(affCode: AffCode) {
    if (!this._utilService.arrayContainsEntry(this.selectedAffCodes, 'AffCode', affCode.AffCode)) {
      const addedAc = new AffCode();
      this._utilService.mapSafe(affCode, addedAc);
      addedAc.Selected = false;
      this.selectedAffCodes.push(addedAc);
    }
    this.sortSelectedAffCodes();
  }

  public removeAffCodes = (): void => {
    let unselected = new Array<AffCode>();

    for (let ac of this.selectedAffCodes) {
      if (ac.Selected == false) {
        unselected.push(ac);
      }
    }
    this.selectedAffCodes = unselected;

    //this.value = new Array<string>();
    //for (let item of unselected) {
    //    this.value.push(item.AffCode);
    //}

    this.updateValueFromSelectedAffCodes();
  };

  public toggleAvailAffCodeSelected = (affCode: AffCode): void => {
    if (affCode.Selected == true) {
      affCode.Selected = false;
    } else {
      affCode.Selected = true;
    }
  };

  public toggleSelectedAffCodeSelected = (affCode: AffCode): void => {
    if (this.isReserved(affCode)) {
      return;
    }
    affCode.Selected = !affCode.Selected;
  };

  ngOnChanges(changes: SimpleChanges) {
    this.updateSelectedAffCodesFromValue();

    let new_allOfSelected: string = '1';

    if (changes['allOfSelected']) new_allOfSelected = this.allOfSelected ? '1' : '0';

    if (new_allOfSelected != this._allOfSelected) this._allOfSelected = new_allOfSelected;
  }
  modelChange(data: Array<string>) {
    this.value = data;
    this.updateSelectedAffCodesFromValue();
    this.emitOutputObject();
  }

  emitOutputObject = (): void => {};

  public updateSelectedAffCodesFromValue = (): void => {
    this.selectedAffCodes = new Array<AffCode>();
    for (let v of this.value) {
      let affCode = new AffCode();
      affCode.AffCode = v;
      this.selectedAffCodes.push(affCode);
    }
    this.sortSelectedAffCodes();
  };

  public updateValueFromSelectedAffCodes = (): void => {
    this.sortSelectedAffCodes();
    this.value = new Array<string>();
    for (let affCode of this.selectedAffCodes) {
      this.value.push(affCode.AffCode);
    }
  };

  /**
   * "all of" checkbox clicked
   */
  public handleAllOfClick(newValue: boolean): void {
    let allOfSelectedOld: boolean = this.allOfSelected;

    this.allOfSelected = newValue;

    if (this.allOfSelected != allOfSelectedOld) this.allOfSelectedChange.emit(this.allOfSelected);
  }

  public isReserved(affCode: AffCode) {
    if (affCode.AffCode == null) {
      return false;
    } else {
      switch (affCode.AffCode.toLowerCase().trim()) {
        case 'all':
        case 'member':
          return true;
        default:
          return false;
      }
    }
  }

  private sortSelectedAffCodes() {
    this.selectedAffCodes.sort((a, b) => {
      if (a.AffCode < b.AffCode) {
        return -1;
      } else if (a.AffCode === b.AffCode) {
        return 0;
      } else {
        return 1;
      }
    });
  }
}
